import type { TimeFormOptions } from '@fos/shared/contracts/time';
import { useMemo } from 'react';
import { trpc } from '../../../lib/trpc';

/**
 * Słowniki formularza czasu (`time.options`): klienci z zakresu osoby, projekty, usługi, członkowie, ostatnie
 * połączenia klient · projekt. Kombinacja „klient · projekt” ma klucz `clientId|projectId` (puste = brak).
 */

export function useTimeOptions(enabled = true) {
  return trpc.time.options.useQuery(undefined, { enabled, staleTime: 60_000 });
}

export interface LinkCombo {
  key: string;
  clientId: string | null;
  projectId: string | null;
  clientName: string | null;
  projectName: string | null;
  color: string | null;
  /** projekt wewnętrzny albo brak klienta */
  internal: boolean;
  recent: boolean;
}

export function comboKey(clientId: string | null | undefined, projectId: string | null | undefined): string {
  return `${clientId ?? ''}|${projectId ?? ''}`;
}

export function splitComboKey(key: string): { clientId: string | null; projectId: string | null } {
  const [c = '', p = ''] = key.split('|');
  return { clientId: c || null, projectId: p || null };
}

/** „HelpMyCar · Aplikacja”, „HelpMyCar”, „Fravity OS (wewnętrzny)”, „Bez klienta”. */
export function comboLabel(c: Pick<LinkCombo, 'clientName' | 'projectName'>): string {
  if (c.clientName && c.projectName) return `${c.clientName} · ${c.projectName}`;
  if (c.clientName) return c.clientName;
  if (c.projectName) return c.projectName;
  return 'Bez klienta';
}

/**
 * Wszystkie połączenia do wyboru: najpierw ostatnio używane, potem projekty klientów (A–Z), klienci bez projektu,
 * projekty wewnętrzne i „Bez klienta”.
 */
export function buildCombos(options: TimeFormOptions | undefined): LinkCombo[] {
  if (!options) return [];
  const clients = new Map(options.clients.map((c) => [c.id, c]));
  const projects = new Map(options.projects.map((p) => [p.id, p]));
  const make = (clientId: string | null, projectId: string | null, recent: boolean): LinkCombo | null => {
    const client = clientId ? clients.get(clientId) : undefined;
    const project = projectId ? projects.get(projectId) : undefined;
    if (clientId && !client) return null;
    if (projectId && !project) return null;
    return {
      key: comboKey(clientId, projectId),
      clientId,
      projectId,
      clientName: client?.name ?? null,
      projectName: project?.name ?? null,
      color: client?.color ?? null,
      internal: !clientId,
      recent,
    };
  };
  const out: LinkCombo[] = [];
  const seen = new Set<string>();
  const push = (c: LinkCombo | null) => {
    if (!c || seen.has(c.key)) return;
    seen.add(c.key);
    out.push(c);
  };
  for (const r of options.recent) push(make(r.clientId, r.projectId, true));
  const sortedProjects = [...options.projects].sort((a, b) => {
    const ca = a.clientId ? (clients.get(a.clientId)?.name ?? '') : '￿';
    const cb = b.clientId ? (clients.get(b.clientId)?.name ?? '') : '￿';
    return ca.localeCompare(cb, 'pl') || a.name.localeCompare(b.name, 'pl');
  });
  for (const p of sortedProjects) if (p.clientId) push(make(p.clientId, p.id, false));
  for (const c of options.clients) push(make(c.id, null, false));
  for (const p of sortedProjects) if (!p.clientId) push(make(null, p.id, false));
  push(make(null, null, false));
  return out;
}

export function useCombos(options: TimeFormOptions | undefined): LinkCombo[] {
  return useMemo(() => buildCombos(options), [options]);
}
